import { MprcRequestErrorDialog, MprcSpinnerIndicator } from "components";
import { ChannelPanelDto, PublicationPanelDto, TopicPanelDto } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FeedBoxPanels } from "views/knowledge/feed/components/FeedBoxPanels";
import { PanelChannels } from "views/knowledge/feed/components/Panel/PanelChannels";
import { PanelLatestPublication } from "views/knowledge/feed/components/Panel/PanelLatestPublication";
import { PanelPublicationTrending } from "views/knowledge/feed/components/Panel/PanelPublicationTrending";
import { PanelTopics } from "views/knowledge/feed/components/Panel/PanelTopics";

interface IProps {
  onEmptyPanel: () => void;
}

/**
 * Painel lateral para o feed "Para Você"
 * @param props
 */
export const FeedStartupRightPanel: React.FC<IProps> = (props) => {
  const api = useCoreApi();
  const [viewError, setViewError] = useState<any>();
  const { t } = useTranslation("knowledge");

  const [publicationTrending, setPublicationTrending] = useState<PublicationPanelDto[]>([]);
  const [latestPublication, setLatestPublication] = useState<PublicationPanelDto[]>([]);
  const [topicsHighlight, setTopicsHighlight] = useState<TopicPanelDto[]>([]);
  const [channelsToSubscribe, setChannelsToSubscribe] = useState<ChannelPanelDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  ////////////////////////////////////////////////////////////////////////////
  /// Effects
  ////////////////////////////////////////////////////////////////////////////

  // Ao carregar
  useEffect(() => {
    if (loading) {
      Promise.all([
        api.v1.knowledgeFeed.getFeedPanelPublicationTrending(),
        api.v1.knowledgeFeed.getFeedPanelLatestPublications(),
        api.v1.knowledgeFeed.getFeedPanelTopicsHighlight(),
        api.v1.knowledgeFeed.getFeedPanelChannelsHighlight()
      ])
        .then((results) => {
          setPublicationTrending(results[0].data);
          setLatestPublication(results[1].data);
          setTopicsHighlight(results[2].data);
          setChannelsToSubscribe(results[3].data);

          if (results[0].data.length == 0
            && results[1].data.length == 0
            && results[2].data.length == 0
            && results[3].data.length == 0) {
            props.onEmptyPanel();
          }
        })
        .catch((error: any) => {
          setViewError(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [loading]);


  const panels = useMemo(() => {
    const data = [];

    if (publicationTrending.length > 0)
      data.push(
        <PanelPublicationTrending
          key="PanelPublicationTrending"
          viewData={publicationTrending}
        />
      );

    if (topicsHighlight.length > 0)
      data.push(
        <PanelTopics
          key="PanelTopics"
          title={t("Feed.Panels.TopicsHighlight.Title")}
          viewData={topicsHighlight}
        />
      );

    if (channelsToSubscribe.length > 0)
      data.push(
        <PanelChannels
          key="PanelChannels"
          title={t("Feed.Panels.ChannelsHighlight.Title")}
          viewData={channelsToSubscribe}
        />
      );

    data.push(
      <PanelLatestPublication
        key="PanelLatestPublication"
        viewData={latestPublication}
      />
    );

    return data;
  }, [publicationTrending, topicsHighlight, channelsToSubscribe]);

  ////////////////////////////////////////////////////////////////////////////
  /// Render
  ////////////////////////////////////////////////////////////////////////////

  return (
    <>
      <MprcRequestErrorDialog
        error={viewError}
        onClose={() => setViewError(undefined)}
      />

      {
        loading
          ? <MprcSpinnerIndicator />
          : <FeedBoxPanels>{panels}</FeedBoxPanels>
      }
    </>
  );
};
